import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Input from '@salesforce/design-system-react/components/input';
import { useDispatch, useSelector } from 'react-redux';
import { apiUserMetaMask } from '../../../../services/auth';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import * as Yup from 'yup';
import Web3 from 'web3';
import { is_metaMask_address_unique } from '../../../../services/account';
import { Form, Formik } from 'formik';
import { Button, Checkbox } from '@salesforce/design-system-react';
import { setUser } from '../../../../store/auth/slice';
import { toast } from 'react-hot-toast';
import { debounce } from 'lodash';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const Details = () => {
  const dispatch = useDispatch();

  const [isMetaMask, setIsMetaMask] = useState(false);
  const [checkUnique, setCheckUnique] = useState({
    isAddressUnique: true,
  });
  const [isUniqueValidating, setIsUniqueValidating] = useState(false);
  const userInfo = useSelector((state) => state.auth.user);

  useEffect(() => {
    setIsMetaMask(userInfo.meta_mask_enabled);
  }, userInfo);

  const onFormSubmit = async (values, setSubmitting) => {
    let data;
    if (isMetaMask) {
      data = {
        meta_mask_enabled: isMetaMask,
        meta_mask_address: values.publicAddress,
      };
    } else {
      data = {
        meta_mask_enabled: false,
      };
    }

    try {
      const result = await apiUserMetaMask(data);
      if (result.status === 200) {
        if (result.data.data) {
          dispatch(setUser(result.data.data));
        }
        toast.success('Successfully updated', {
          id: 'meta-mask-success',
        });
      } else {
        toast.error('Something went wrong!', {
          id: 'meta-mask-error',
        });
      }
    } catch (error) {}

    setSubmitting(false);
  };

  const onSwitcherToggle = (val) => {
    if (val.target.checked === false) {
      setIsMetaMask(false);
    } else {
      setIsMetaMask(true);
    }
  };

  const validationSchema = isMetaMask
    ? Yup.object().shape({
        publicAddress: Yup.string()
          .required('Enter your Metamask public address')
          .test('test-w3address', 'The Metamask public address is not valid', (value) =>
            Web3.utils.isAddress((value || '').toLowerCase())
          ),
      })
    : Yup.object().shape({
        publicAddress: Yup.string(),
      });

  const resetUniqueStatus = (fieldName) => {
    switch (fieldName) {
      case 'publicAddress':
        setCheckUnique({
          ...checkUnique,
          isAddressUnique: true,
        });
        return;
      default:
        return;
    }
  };

  const validateAddress = async (value, fieldName, setFieldError) => {
    if (!value) {
      return;
    }
    if (!Web3.utils.isAddress((value || '').toLowerCase())) {
      setFieldError(fieldName, 'The Metamask public address is not valid');
      return;
    }
    const response = await is_metaMask_address_unique(value.trim());
    const responseData = response.data.data;

    if (responseData) {
      setIsUniqueValidating(false);
      setCheckUnique((prevState) => ({
        ...prevState,
        isAddressUnique: responseData.unique,
      }));
      if (!responseData.unique) {
        setIsUniqueValidating(true);
        setFieldError(fieldName, 'Public address already exists');
      }
    }
  };

  //debounce validator functions
  const handleDebounceFn = useCallback((value, fieldName, setFieldError) => {
    setFieldError(fieldName); //clear any existing validation errors
    setIsUniqueValidating(true);
    switch (fieldName) {
      case 'publicAddress':
        validateAddress(value, fieldName, setFieldError);
        return;
      default:
        return;
    }
  }, []);

  const debouncedValidator = useMemo(() => debounce(handleDebounceFn, 500), []);

  return (
    <Formik
      enableReinitialize
      initialValues={{
        publicAddress: userInfo.meta_mask_address ?? '',
      }}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting }) => {
        setSubmitting(true);
        onFormSubmit(values, setSubmitting);
      }}
    >
      {({
        values,
        touched,
        errors,
        handleBlur,
        isSubmitting,
        setFieldError,
        handleChange,
        setFieldTouched,
      }) => {
        const validatorProps = { touched, errors };

        const handleChangeValidation = (e) => {
          handleChange(e);
          setFieldTouched(e.target.name, true, false);
          resetUniqueStatus(e.target.name);
          debouncedValidator(e.target.value, e.target.name, setFieldError);
        };

        return (
          <IconSettings iconPath="/assets/icons">
            <Form>
              <div className="add-form">
                <section className="slds-p-around_large">
                  <div>
                    <Checkbox
                      labels={{
                        label: 'Enable MetaMask',
                      }}
                      id="checkbox-toggle-example"
                      variant="toggle"
                      checked={isMetaMask}
                      onChange={(checked) => onSwitcherToggle(checked)}
                    />
                  </div>

                  {isMetaMask && (
                    <>
                      <Input
                        id="publicAddress"
                        name="publicAddress"
                        label="Public Address"
                        placeholder="Public Address"
                        value={values.publicAddress || ''}
                        onBlur={handleBlur}
                        className={classNames(
                          validatorProps.errors.publicAddress &&
                            validatorProps.touched.publicAddress &&
                            'slds-has-error'
                        )}
                        onChange={handleChangeValidation}
                        onKeyDown={(e) => {
                          e.key === 'Enter' && e.preventDefault();
                        }}
                      />
                      {validatorProps.errors.publicAddress &&
                        validatorProps.touched.publicAddress && (
                          <div className="slds-form-element__help">
                            {validatorProps.errors.publicAddress}
                          </div>
                        )}
                    </>
                  )}
                </section>

                <div style={{ paddingLeft: '25px', paddingBottom: '20px' }}>
                  <Button
                    label={isSubmitting ? <span>Loading</span> : <span>Update</span>}
                    type="submit"
                    variant="brand"
                    disabled={isSubmitting || (isUniqueValidating && isMetaMask)}
                  />
                </div>
              </div>
            </Form>
          </IconSettings>
        );
      }}
    </Formik>
  );
};

export default Details;
